import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../../components/feature/Layout';
import { useSEO, generateWebPageSchema } from '../../../utils/seo';

const MathSummaryPage: React.FC = () => {
  // FAQ設定
  const faqs = [
    {
      question: '数学が苦手な子は、どこから始めればいいですか？',
      answer: 'まずは「計算の基礎」から固めましょう。正負の数、文字式、方程式など、中1の計算単元がしっかりできていないと、関数や図形でもつまずきます。無料プリントで基本問題を繰り返し解き、「できた」という実感を積み重ねることが大切です。',
    },
    {
      question: '関数が全く分からない場合、どう教えればいいですか？',
      answer: '関数は「xが変わるとyも変わる」という対応関係を理解することが第一歩です。まずは表やグラフを使って、具体的な数値で対応を確認させましょう。式だけで教えようとせず、視覚的に理解させることがポイントです。',
    },
    {
      question: '図形の証明問題が苦手な子への声かけは？',
      answer: '「何を使えば証明できるか」を一緒に考える声かけが効果的です。「合同条件は3つあるけど、どれが使えそう？」「この角度、どうやって求められる？」と、段階的に問いかけることで、自分で考える力が育ちます。',
    },
    {
      question: '中学数学の無料教材サイトは、どれを選べばいいですか？',
      answer: '基礎固めなら「ちびむすドリル」、高校受験対策なら「無料で使える中学学習プリント」がおすすめです。動画で学びたい場合は「平均ドリル」や「eboard」が便利です。複数のサイトを組み合わせて使うと、より効果的です。',
    },
    {
      question: '居場所や家庭で数学を教える際の注意点は？',
      answer: '「答えを教える」のではなく、「考え方を一緒に整理する」ことを意識しましょう。間違えても責めず、「どこまで分かった？」「次はどうすればいい？」と問いかけることで、子どもが自分で考える習慣がつきます。',
    },
  ];

  // SEO設定
  useSEO({
    title: '中学数学まとめ｜計算・関数・図形の学習ガイド｜キョウコレ',
    description: '中学数学の学習内容を学年別・単元別に整理。計算、関数、図形のつまずきやすいポイントと、おすすめの無料教材サイトをご紹介します。',
    keywords: '中学数学,数学まとめ,計算,関数,図形,つまずき,学習ガイド,無料教材',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/features/math-summary',
    jsonLd: generateWebPageSchema(
      '中学数学まとめ｜計算・関数・図形の学習ガイド｜キョウコレ',
      '中学数学の学習内容を学年別・単元別に整理。計算、関数、図形のつまずきやすいポイントと、おすすめの無料教材サイトをご紹介します。',
      '/features/math-summary'
    ),
  });

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* ヘッダー */}
          <div className="text-center mb-12">
            <div className="inline-block w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center text-4xl mb-4">
              📐
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              中学数学の無料教材まとめ
            </h1>
            <p className="text-lg text-gray-600">
              学年別・単元別に整理した学習ガイドと、おすすめの無料教材サイトをご紹介
            </p>
          </div>

          {/* このページでわかること */}
          <div className="bg-white rounded-xl shadow-sm p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-4 border-blue-500 pb-2">
              このページでわかること
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>中学数学の単元を「計算→関数→図形」の流れで整理する方法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>学年別・単元別のつまずきポイントと攻略法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>無料で使える数学教材サイトの特徴と使い分け</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>居場所や家庭で数学を教える際のコツ</span>
              </li>
            </ul>
          </div>

          {/* 数学はこう整理すると進めやすい */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-blue-500 pb-2">
              数学はこう整理すると進めやすい
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                中学数学は「計算」「関数」「図形」の3つの柱で構成されています。この3つは独立しているようで、実は深くつながっています。例えば、関数のグラフを描くには計算力が必要ですし、図形の証明では方程式を使うこともあります。
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>つまずき単元マップ：</strong>数学が苦手な子の多くは、「計算の基礎」でつまずいています。正負の数、文字式、方程式がしっかりできていないと、関数や図形でも苦労します。まずは計算の土台を固めることが、数学全体の理解につながります。
              </p>
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">学習の進め方（推奨順序）</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</span>
                    <div>
                      <strong>計算の基礎固め</strong>：正負の数、文字式、方程式を繰り返し練習
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</span>
                    <div>
                      <strong>関数の理解</strong>：比例・反比例から始めて、一次関数、二次関数へ
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</span>
                    <div>
                      <strong>図形の攻略</strong>：作図・計量から証明へ、段階的に進める
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">4</span>
                    <div>
                      <strong>総合問題</strong>：複数の単元を組み合わせた応用問題に挑戦
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* 学年別・単元別の整理 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-blue-500 pb-2">
              学年別・単元別の整理
            </h2>

            {/* 中1数学 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3">1</span>
                中1数学のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中1では「正負の数」「文字式」「方程式」という計算の基礎を学びます。ここでつまずくと、中2・中3で苦労するため、丁寧に進めることが大切です。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">正負の数</h4>
                  <p className="text-sm text-gray-600">
                    マイナスの概念を理解し、四則演算をマスターする。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">文字式</h4>
                  <p className="text-sm text-gray-600">
                    文字を使った式の表し方と計算方法を学ぶ。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">方程式</h4>
                  <p className="text-sm text-gray-600">
                    一次方程式の解き方と文章題への応用。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">比例・反比例</h4>
                  <p className="text-sm text-gray-600">
                    関数の入門。表・式・グラフの3つの表現方法を理解する。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">平面図形・空間図形</h4>
                  <p className="text-sm text-gray-600">
                    作図、面積・体積の計算を学ぶ。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中2数学 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3">2</span>
                中2数学のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中2では「連立方程式」「一次関数」「図形の証明」など、応用力が求められる単元が増えます。中1の基礎がしっかりしていれば、スムーズに進められます。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">式の計算</h4>
                  <p className="text-sm text-gray-600">
                    単項式・多項式の計算、等式の変形を学ぶ。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">連立方程式</h4>
                  <p className="text-sm text-gray-600">
                    2つの式を組み合わせて解く方法を習得。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">一次関数</h4>
                  <p className="text-sm text-gray-600">
                    y=ax+bの形で表される関数。グラフの傾きと切片を理解する。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">図形の性質と証明</h4>
                  <p className="text-sm text-gray-600">
                    合同条件を使った証明問題。論理的思考力が試される。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">確率</h4>
                  <p className="text-sm text-gray-600">
                    場合の数と確率の基本を学ぶ。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中3数学 */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3">3</span>
                中3数学のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中3では「二次方程式」「二次関数」「相似」など、高校数学につながる重要単元を学びます。高校受験対策も本格化するため、計画的な学習が必要です。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">展開と因数分解</h4>
                  <p className="text-sm text-gray-600">
                    式を展開したり、因数分解したりする技術を習得。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">平方根</h4>
                  <p className="text-sm text-gray-600">
                    √（ルート）の計算と性質を理解する。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">二次方程式</h4>
                  <p className="text-sm text-gray-600">
                    因数分解や解の公式を使って解く。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">二次関数</h4>
                  <p className="text-sm text-gray-600">
                    y=ax²の形で表される関数。放物線のグラフを理解する。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">相似</h4>
                  <p className="text-sm text-gray-600">
                    相似条件を使った証明と、相似比を使った計算。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">三平方の定理</h4>
                  <p className="text-sm text-gray-600">
                    直角三角形の辺の関係を使った計算。<Link to="/materials?subject=数学" className="text-blue-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* おすすめ無料教材サイト */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-blue-500 pb-2">
              おすすめ無料教材サイト一覧
            </h2>
            <p className="text-gray-700 mb-6">
              中学数学の学習に役立つ無料教材サイトを厳選しました。それぞれの特徴を理解して、目的に合わせて使い分けましょう。
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://chugaku.manabihiroba.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                  >
                    無料で使える中学学習プリント
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>基礎から入試レベルまで幅広く対応。体系的に学習できる
                </p>
                <p className="text-sm text-gray-700">
                  中1から中3まで、すべての単元のプリントが揃っています。基本問題から応用問題まで段階的に用意されているため、自分のレベルに合わせて学習できます。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://math.005net.com/mondai.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                  >
                    中学校数学学習サイト
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>難関校向けの応用問題も充実。高校受験対策に最適
                </p>
                <p className="text-sm text-gray-700">
                  基礎から難関校レベルまで、幅広い難易度の問題が用意されています。特に、高校受験の応用問題を解きたい生徒におすすめです。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://happylilac.net/jhs-math1.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                  >
                    ちびむすドリル 中学数学
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>学年別の基礎固めに最適。印刷しやすいレイアウト
                </p>
                <p className="text-sm text-gray-700">
                  学年別・単元別に整理されたプリントが豊富です。基礎固めに特化しており、数学が苦手な子でも取り組みやすい内容です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://heikin.space/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                  >
                    平均ドリル
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>無料授業動画とプリントがセット。自習向け
                </p>
                <p className="text-sm text-gray-700">
                  授業動画を見てから問題を解く形式で、自宅学習に最適です。動画は短くまとまっており、集中力が続きやすい工夫がされています。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <Link
                    to="/sites"
                    className="text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                  >
                    その他の数学教材サイトを見る
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </h3>
                <p className="text-sm text-gray-700">
                  キョウコレでは、さらに多くの無料教材サイトを紹介しています。教科や単元で絞り込んで検索できます。
                </p>
              </div>
            </div>
          </section>

          {/* 居場所・家庭での使い方 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-blue-500 pb-2">
              居場所・家庭での使い方
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                数学は「答えを教える」のではなく、「考え方を一緒に整理する」ことが大切です。居場所や家庭で数学を教える際のコツをまとめました。
              </p>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">1. 「どこまで分かった？」と問いかける</h3>
                  <p className="text-gray-700 leading-relaxed">
                    間違えた問題を見つけたら、すぐに答えを教えるのではなく、「どこまで分かった？」と聞いてみましょう。子ども自身が「ここまでは分かる」「ここから分からない」を言語化することで、つまずきポイントが明確になります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">2. 図や表を使って視覚化する</h3>
                  <p className="text-gray-700 leading-relaxed">
                    関数や図形の問題は、言葉だけで説明しても伝わりにくいものです。図や表を一緒に描きながら、視覚的に理解させることがポイントです。「この部分、図に描いてみようか」と声をかけると、子どもも理解しやすくなります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3. 「できた」を積み重ねる</h3>
                  <p className="text-gray-700 leading-relaxed">
                    数学が苦手な子は、「どうせできない」と諦めがちです。まずは簡単な問題から始めて、「できた」という実感を積み重ねることが大切です。「この問題、前より解けるようになったね」と成長を認める声かけが、学習意欲を高めます。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">4. 活用レシピを参考にする</h3>
                  <p className="text-gray-700 leading-relaxed">
                    キョウコレの<Link to="/recipes" className="text-blue-600 hover:underline">活用レシピ</Link>では、他の居場所や家庭での実践例が共有されています。「こんな声かけが効果的だった」「この教材の使い方が良かった」といったアイデアを参考にしてみましょう。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-blue-500 pb-2">
              よくある質問
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start">
                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      Q
                    </span>
                    {faq.question}
                  </h3>
                  <div className="ml-11">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ボラタス導線 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-blue-500 pb-2">
              オンライン学習支援を探すなら（ボラタス）
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                教材だけでなく、オンラインで学習をサポートしてくれるボランティアを探すこともできます。居場所や家庭での学習支援に、ぜひご活用ください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://boratasu.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスでボランティアを探す
                  <i className="ri-external-link-line ml-2"></i>
                </a>
                <a
                  href="https://zion-craft.work/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-slate-600 text-white font-medium rounded-lg hover:bg-slate-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスについて詳しく知る
                  <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>
          </section>

          {/* まとめ */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              中学数学は、整理すれば必ず分かるようになります
            </h2>
            <p className="text-lg leading-relaxed mb-6">
              このページで紹介した無料教材サイトや学習のコツを活用して、<br />
              子どもたちの「分かった！」を増やしていきましょう。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/materials?subject=数学"
                className="inline-block px-6 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                数学の単元ガイド一覧を見る
              </Link>
              <Link
                to="/sites"
                className="inline-block px-6 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                教材サイト一覧を見る
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MathSummaryPage;
